import React, { type ReactNode } from "react";

import clsx from "clsx";
import { isEqual as datesAreEqual, format, parseISO } from "date-fns";

import { useUser } from "@components/user-context";
import { useOverviewContext } from "@features/overview-v2/hooks";
import {
  type TileDescriptor,
  getTileTypeLabel,
} from "@features/overview-v2/types";
import {
  type DateInterval,
  emptyText,
  MCAttributionWindowDays,
  NBAccountingMode,
  TimeGranularityV2,
} from "@north-beam/nb-common";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

const DATE_FORMAT = "MMM dd, yyyy";

function formatDateRange({ startDate, endDate }: DateInterval) {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  if (datesAreEqual(start, end)) {
    return format(start, DATE_FORMAT);
  } else {
    return `${format(start, DATE_FORMAT)} – ${format(end, DATE_FORMAT)}`;
  }
}

interface SubDetailProps {
  readonly label: ReactNode;
  readonly children: ReactNode;
}

const SubDetail = ({ label, children }: SubDetailProps): JSX.Element => (
  <div className="flex flex-col gap-1">
    {label}
    {children}
  </div>
);

const Indicator = () => (
  <div className="relative h-[100%] left-[2px]">
    <span
      className={clsx(
        "inline-block rounded-full w-[0.5rem] h-[0.5rem] bg-mars-1",
        "absolute top-1",
      )}
    />
  </div>
);

const Detail = ({
  label,
  showIndicator,
  children,
}: SubDetailProps & { readonly showIndicator?: boolean }): JSX.Element => (
  <div className="flex flex-row gap-4 min-h-[28px] items-top font-light text-v2-secondary">
    <div className="flex flex-row shrink-0 w-[200px]">
      {label}
      {showIndicator && <Indicator />}
    </div>
    <div className="flex flex-col gap-2 text-dark font-normal">{children}</div>
  </div>
);

interface ItemizedSectionProps<T> {
  readonly label: ReactNode;
  readonly items: T[] | null | undefined;
  readonly emptyMessage: string;
  readonly children: (item: T) => ReactNode;
}

const ItemizedSection = <T,>({
  label,
  items,
  children,
  emptyMessage,
}: ItemizedSectionProps<T>): JSX.Element => (
  <div className="flex flex-col gap-1 pl-2">
    <div className="flex flex-row text-dark text-sm font-normal">{label}</div>
    {items && items.length !== 0 ? (
      items.map((item, i) => (
        <div key={i} className="text-sm text-v2-secondary font-light pl-2">
          {children(item)}
        </div>
      ))
    ) : (
      <div className="text-sm text-v2-secondary font-light pl-2">
        {emptyMessage}
      </div>
    )}
  </div>
);

const TilesDetail = ({
  tiles,
  showIndicator,
}: {
  tiles: TileDescriptor[];
  showIndicator?: boolean;
}): JSX.Element => {
  const { metrics } = useOverviewContext();

  if (tiles.length === 0) {
    return <Detail label="Tiles">{emptyText}</Detail>;
  }

  const getTileSectionLabel = (t: TileDescriptor) => {
    const metric = metrics.find((m) => m.id === t.metricId);

    return (
      <div className="flex flex-row gap-1">
        {metric ? (
          <>
            {metric.name}
            <span className="text-v2-secondary font-light">{`(${getTileTypeLabel(
              t.type,
            )})`}</span>
          </>
        ) : (
          getTileTypeLabel(t.type)
        )}
      </div>
    );
  };

  return (
    <Detail label="Tiles" showIndicator={showIndicator}>
      {tiles.map((t) => (
        <SubDetail key={t.id} label={getTileSectionLabel(t)}>
          <ItemizedSection
            label="Filters"
            items={t.filters}
            emptyMessage={emptyText}
          >
            {(filter) => (
              <div className="flex flex-row gap-2">
                {filter.breakdown}
                <span className="text-dark font-normal">{filter.value}</span>
              </div>
            )}
          </ItemizedSection>
        </SubDetail>
      ))}
    </Detail>
  );
};

export const ConfigurationDetails = () => {
  const { isDemoMode } = usePrefs();
  const {
    selectedSavedView,
    unsavedFilters,
    tiles,
    filters: {
      dateRangeInterval,
      compareDateRangeInterval,
      timeGranularity,
      accountingMode,
      attributionModel,
      attributionWindow,
    },
  } = useOverviewContext();
  const { user } = useUser();
  const attributionMethodEnum = user.attributionMethodEnum;

  return (
    <div className="flex flex-col gap-7">
      <Detail label="State saved?">
        <span
          className={clsx("border rounded text-sm px-2.5 py-0.5", {
            "bg-sun-2 border-sun+2 text-[#996800]":
              unsavedFilters !== null && unsavedFilters.size !== 0,
            "bg-alien-2 border-alien+2 text-[#3B8139]":
              unsavedFilters === null || unsavedFilters.size === 0,
          })}
        >
          {unsavedFilters === null || unsavedFilters.size === 0 ? "Yes" : "No"}
        </span>
      </Detail>
      <Detail label="Saved view">
        {generateAnonBase64Hash({
          value: selectedSavedView?.name,
          prefix: "quick-filter",
          doAnon: isDemoMode && !selectedSavedView?.is_template,
        })}
      </Detail>
      <TilesDetail tiles={tiles} />
      <Detail
        label="Attribution Model"
        showIndicator={unsavedFilters?.has("attributionModel")}
      >
        {attributionMethodEnum.getLabel(attributionModel)}
      </Detail>
      {accountingMode === "accrual" && (
        <Detail
          label="Attribution Window"
          showIndicator={unsavedFilters?.has("attributionWindow")}
        >
          {MCAttributionWindowDays.getLabel(attributionWindow)}
        </Detail>
      )}
      <Detail
        label="Accounting Mode"
        showIndicator={unsavedFilters?.has("accountingMode")}
      >
        {NBAccountingMode.getLabel(accountingMode)}
      </Detail>
      <Detail
        label="Time granularity"
        showIndicator={unsavedFilters?.has("timeGranularity")}
      >
        {TimeGranularityV2.getLabel(timeGranularity)}
      </Detail>
      <Detail
        label="Time period"
        showIndicator={unsavedFilters?.has("dateRange")}
      >
        {formatDateRange(dateRangeInterval)}
      </Detail>
      <Detail
        label="Compare time period"
        showIndicator={unsavedFilters?.has("compareDateRange")}
      >
        {compareDateRangeInterval
          ? formatDateRange(compareDateRangeInterval)
          : "-"}
      </Detail>
    </div>
  );
};
